import React, { useId, useState } from "react";

import { useTranslation } from "react-i18next";

import { isValidHttpUrl } from "@/src/utils/url";

import AdParamsButton from "./AdParamsButton";
import { TextFieldBase } from "../form-fields/TextField";
import { Field, FieldDescription, FieldLabel } from "../ui/field";
import { Input } from "../ui/input";
import SwitchWithLabel from "../utils/SwitchWithLabel";

import type { ChangeEvent, JSX } from "react";

import type { IAdvertisingSingle, VASTFormChange } from "../../types";

interface PauseAdParametersProps {
  onChange: VASTFormChange;
  campaign: IAdvertisingSingle;
}

/** What the operator is editing, before it is worth publishing upwards. */
interface PauseAdDraft {
  isEnabled: boolean;
  url: string;
  debounceMs?: number;
  cooldownMs?: number;
}

/** The player's own defaults, shown as placeholders rather than imposed. */
const DEFAULT_DEBOUNCE_SECONDS = 3;
const DEFAULT_COOLDOWN_SECONDS = 30;

const MIN_DEBOUNCE_SECONDS = 0.5;
const MAX_DEBOUNCE_SECONDS = 10;
const MIN_COOLDOWN_SECONDS = 0;
const MAX_COOLDOWN_SECONDS = 3600;

interface SecondsFieldProps {
  className?: string;
  label: string;
  description: string;
  /** Stored in milliseconds; edited in seconds. */
  valueMs?: number;
  placeholderSeconds: number;
  minSeconds: number;
  maxSeconds: number;
  onChange: (valueMs?: number) => void;
}

/**
 * A duration the operator reads in seconds and the player consumes in
 * milliseconds. Left empty, it publishes `undefined` so the player keeps its own
 * default instead of freezing today's value into the campaign.
 */
function SecondsField({
  className,
  label,
  description,
  valueMs,
  placeholderSeconds,
  minSeconds,
  maxSeconds,
  onChange,
}: SecondsFieldProps): JSX.Element {
  const id = useId();

  const handleChange = (DOMEvent: ChangeEvent<HTMLInputElement>): void => {
    const { value } = DOMEvent.target;

    onChange(value === "" ? undefined : Number(value) * 1000);
  };

  return (
    <Field className={className}>
      <FieldLabel htmlFor={id}>{label}</FieldLabel>
      <Input
        id={id}
        max={maxSeconds}
        min={minSeconds}
        onChange={handleChange}
        placeholder={String(placeholderSeconds)}
        step={0.5}
        type="number"
        value={valueMs === undefined ? "" : valueMs / 1000}
      />
      <FieldDescription>{description}</FieldDescription>
    </Field>
  );
}

/**
 * Pause-ad section of a VAST campaign.
 *
 * Held as a local draft because `pause_ad` is only published once the ad is both
 * enabled *and* pointed at an endpoint: the campaign field stays `null` until
 * then, and SwitchOnOff mirrors its `checked` prop, so a switch derived from the
 * campaign would flip back off under an operator who turns it on before typing.
 */
function PauseAdParameters({
  onChange,
  campaign,
}: PauseAdParametersProps): JSX.Element {
  const { t } = useTranslation("editor-advertisings");
  const [draft, setDraft] = useState<PauseAdDraft>({
    isEnabled: Boolean(campaign.pause_ad),
    url: campaign.pause_ad?.url ?? "",
    debounceMs: campaign.pause_ad?.debounce_ms,
    cooldownMs: campaign.pause_ad?.cooldown_ms,
  });

  /**
   * Single writer: takes the whole next draft, so no call site can mix up which
   * half is current and which is new.
   */
  const update = (next: PauseAdDraft): void => {
    setDraft(next);
    onChange({
      key: "pause_ad",
      value:
        next.isEnabled && next.url
          ? {
              url: next.url,
              debounce_ms: next.debounceMs,
              cooldown_ms: next.cooldownMs,
            }
          : null,
    });
  };

  const isURLValid = !draft.url || isValidHttpUrl(draft.url);

  return (
    <div className="flex flex-col gap-[0.78125rem] pt-[0.78125rem] pb-[2.5rem]">
      <div className="text-foreground text-[0.9375rem] font-medium">
        {t("pauseAd.sectionTitle")}
      </div>
      <div className="flex flex-col gap-[1.5625rem]">
        <SwitchWithLabel
          isChecked={draft.isEnabled}
          label={t("pauseAd.enable")}
          name="pause_ad_enabled"
          onChange={(isEnabled): void => update({ ...draft, isEnabled })}
        />

        {draft.isEnabled ? (
          <React.Fragment>
            <div className="flex w-full flex-col gap-[0.46875rem]">
              <TextFieldBase
                description={t("pauseAd.hint")}
                error={isURLValid ? undefined : t("invalidVastUrl")}
                label={t("pauseAd.url")}
                onChange={(url): void => update({ ...draft, url })}
                value={draft.url}
              />
              <AdParamsButton url={draft.url} />
            </div>
            <SecondsField
              className="max-w-[350px]"
              description={t("pauseAd.debounceHint")}
              label={t("pauseAd.debounce")}
              maxSeconds={MAX_DEBOUNCE_SECONDS}
              minSeconds={MIN_DEBOUNCE_SECONDS}
              onChange={(debounceMs): void => update({ ...draft, debounceMs })}
              placeholderSeconds={DEFAULT_DEBOUNCE_SECONDS}
              valueMs={draft.debounceMs}
            />
            <SecondsField
              className="max-w-[350px]"
              description={t("pauseAd.cooldownHint")}
              label={t("pauseAd.cooldown")}
              maxSeconds={MAX_COOLDOWN_SECONDS}
              minSeconds={MIN_COOLDOWN_SECONDS}
              onChange={(cooldownMs): void => update({ ...draft, cooldownMs })}
              placeholderSeconds={DEFAULT_COOLDOWN_SECONDS}
              valueMs={draft.cooldownMs}
            />
          </React.Fragment>
        ) : null}
      </div>
    </div>
  );
}

export default PauseAdParameters;
